import { Button, useConfirm, useToast } from '@/components';
import type { DeviceRow } from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { AssignDeviceModal } from './AssignDeviceModal';
import { useAssignDevice } from './hooks';

export const AssignmentPanel = (props: { device: DeviceRow }) => {
  const [showModal, setShowModal] = createSignal(false);
  const assign = useAssignDevice(props.device.id);
  const toast = useToast();
  const confirmDialog = useConfirm();

  const onUnassign = async () => {
    if (!(await confirmDialog({ title: 'Unassign this device?', confirmLabel: 'Unassign' })))
      return;
    try {
      await assign.mutateAsync({ userId: null });
      toast.success('Device unassigned');
    } catch {
      toast.error('Unassign failed');
    }
  };

  return (
    <section class="rounded-xl border border-neutral-100 bg-surface p-4 shadow-sm dark:border-neutral-800 dark:bg-surface-dark">
      <h2 class="mb-3 text-base font-semibold">Assignment</h2>
      <Show
        when={props.device.assignedUser}
        fallback={
          <div class="flex flex-wrap items-center justify-between gap-2">
            <p class="text-sm text-neutral-500">Unassigned</p>
            <Button size="sm" onClick={() => setShowModal(true)}>
              Assign
            </Button>
          </div>
        }
      >
        {(assignee) => (
          <div class="flex flex-wrap items-start justify-between gap-2">
            <div class="min-w-0 flex-1 text-sm">
              <p class="break-words font-medium">{assignee().displayName || assignee().email}</p>
              <p class="break-all text-xs text-neutral-500">{assignee().email}</p>
            </div>
            <div class="flex shrink-0 gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShowModal(true)}>
                Reassign
              </Button>
              <Button size="sm" variant="danger" onClick={onUnassign} loading={assign.isPending}>
                Unassign
              </Button>
            </div>
          </div>
        )}
      </Show>

      <AssignDeviceModal
        open={showModal()}
        onClose={() => setShowModal(false)}
        deviceId={props.device.id}
        currentAssigneeId={props.device.assignedUserId}
      />
    </section>
  );
};
